import { calculateEarnedXp, type GetXpRewardOptions } from './trainingRewards';
import type { SessionMode, TrainingMode } from '../types';

export interface TrainingResultItem {
  id: string;
  label: string;
  prompt?: string;
  correct: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  timeSpent: number;
}

export interface TrainingCompletionPayload {
  results: TrainingResultItem[];
  totalTime: number;
  accuracy: number;
  correctAnswers: number;
  verbsLearned: number;
  xpEarned: number;
}

export interface CompletedTrainingPayload extends TrainingCompletionPayload {
  mode: TrainingMode;
  sessionMode: SessionMode;
}

export interface TrainingResultsState {
  results: TrainingResultItem[];
  totalTime: number;
  accuracy: number;
  correctAnswers: number;
  verbsLearned: number;
  mode: TrainingMode;
  sessionMode: SessionMode;
  xpEarned: number;
}

interface BuildTrainingCompletionPayloadInput {
  mode: TrainingMode;
  results: TrainingResultItem[];
  totalTime: number;
  verbsLearned?: number;
  xpOptions?: GetXpRewardOptions;
}

function normalizeResults(results: TrainingResultItem[] | undefined) {
  return Array.isArray(results) ? results : [];
}

function normalizeTotalTime(totalTime: number) {
  return Number.isFinite(totalTime) ? Math.max(0, totalTime) : 0;
}

function normalizeAccuracy(accuracy: number, results: TrainingResultItem[]) {
  if (!Number.isFinite(accuracy)) {
    return calculateAccuracy(results);
  }

  return Math.min(Math.max(0, accuracy), 100);
}

function normalizeCount(value: number | undefined, fallback: number) {
  const normalizedValue = typeof value === 'number' && Number.isFinite(value) ? value : fallback;

  return Math.max(0, Math.round(normalizedValue));
}

export function countCorrectAnswers(results: TrainingResultItem[]) {
  return normalizeResults(results).filter((item) => item.correct).length;
}

export function countIncorrectAnswers(results: TrainingResultItem[]) {
  const normalizedResults = normalizeResults(results);

  return Math.max(0, normalizedResults.length - countCorrectAnswers(normalizedResults));
}

export function calculateAccuracy(results: TrainingResultItem[]) {
  const normalizedResults = normalizeResults(results);

  if (normalizedResults.length === 0) {
    return 0;
  }

  return (countCorrectAnswers(normalizedResults) / normalizedResults.length) * 100;
}

export function buildTrainingCompletionPayload({
  mode,
  results,
  totalTime,
  verbsLearned,
  xpOptions,
}: BuildTrainingCompletionPayloadInput): TrainingCompletionPayload {
  const normalizedResults = normalizeResults(results);
  const correctAnswers = countCorrectAnswers(normalizedResults);
  const accuracy = calculateAccuracy(normalizedResults);

  return {
    results: normalizedResults,
    totalTime: normalizeTotalTime(totalTime),
    accuracy,
    correctAnswers,
    verbsLearned: normalizeCount(verbsLearned, correctAnswers),
    xpEarned: calculateEarnedXp(mode, accuracy, xpOptions),
  };
}

export function buildTrainingResultsState(payload: CompletedTrainingPayload): TrainingResultsState {
  const results = normalizeResults(payload.results);
  const correctAnswers = normalizeCount(payload.correctAnswers, countCorrectAnswers(results));
  const accuracy = normalizeAccuracy(payload.accuracy, results);

  return {
    results,
    totalTime: normalizeTotalTime(payload.totalTime),
    accuracy,
    correctAnswers,
    verbsLearned: normalizeCount(payload.verbsLearned, correctAnswers),
    mode: payload.mode,
    sessionMode: payload.sessionMode,
    xpEarned: normalizeCount(payload.xpEarned, calculateEarnedXp(payload.mode, accuracy)),
  };
}